import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { inputClass } from "@/lib/constants";
import AdminSlots from "@/pages/admin/Slots";
import AdminDelivery from "@/pages/admin/Delivery";

const TABS = [
  ["collection", "Collection settings", "tab-collection"],
  ["deliveryweek", "Weekly delivery slot", "tab-delivery-week"],
  ["slots", "Slots & times", "tab-slots"],
  ["delivery", "Delivery areas & fees", "tab-delivery"],
];

function mondayIso(offsetWeeks = 0) {
  const london = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/London" }));
  const day = (london.getDay() + 6) % 7;
  london.setDate(london.getDate() - day + offsetWeeks * 7);
  const pad = (n) => String(n).padStart(2, "0");
  return `${london.getFullYear()}-${pad(london.getMonth() + 1)}-${pad(london.getDate())}`;
}

function WeeklyDelivery() {
  const [week, setWeek] = useState(mondayIso(0));
  const [day, setDay] = useState("saturday");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("12:00");
  const [capacity, setCapacity] = useState("10");
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/slots", { params: { weeks: 8 } }).then((r) => setSlots(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const weekSlots = slots.filter((s) => s.type === "delivery" && s.week_start_date === week);
  const published = weekSlots.find((s) => s.active);
  const weekLabel = new Date(`${week}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const shift = (weeks) => {
    const d = new Date(`${week}T12:00:00`);
    d.setDate(d.getDate() + weeks * 7);
    const pad = (n) => String(n).padStart(2, "0");
    setWeek(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  };

  const publish = () => {
    setBusy(true);
    api.post("/admin/slots/delivery-week", {
      week_start_date: week, day, start_time: start, end_time: end, capacity: parseInt(capacity, 10),
    }).then((r) => { toast.success(r.data.note || "Delivery slot published."); load(); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  const toggleSlot = (slot) => {
    const action = slot.active ? "pause" : "resume";
    api.post(`/admin/slots/${slot.id}/${action}`)
      .then(() => { toast.success(slot.active ? "Delivery slot paused — customers can't book it." : "Delivery slot reopened."); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="mt-6 space-y-5" data-testid="weekly-delivery-section">
      <div className="rounded-3xl border border-choco-line bg-choco-light p-6">
        <h2 className="font-serif text-lg font-semibold text-cream-text">This week's delivery day</h2>
        <p className="mt-1 text-xs text-cream-muted">
          Choose ONE delivery day each week — Saturday or Sunday morning. Bookings close at 6:00pm on the Wednesday before, automatically.
          If you don't publish a slot, customers see that delivery is unavailable that week and are offered collection.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={() => shift(-1)} className="min-h-[44px] rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="delivery-week-prev">← Previous week</button>
          <span className="text-sm font-semibold text-cream-text" data-testid="delivery-week-label">Week of {weekLabel}</span>
          <button onClick={() => shift(1)} className="min-h-[44px] rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="delivery-week-next">Next week →</button>
        </div>

        {published ? (
          <div className="mt-4 rounded-2xl border border-green-500/40 bg-green-500/10 p-4" data-testid="delivery-slot-published">
            <p className="text-sm font-bold text-green-300">
              Published: {new Date(`${published.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })},{" "}
              {published.start_time}–{published.end_time} · up to {published.capacity} orders
              {published.booked > 0 && ` · ${published.booked} already booked`}
            </p>
            <button onClick={() => { if (window.confirm("Pause this delivery slot? Customers won't be able to book it.")) toggleSlot(published); }}
              className="mt-3 min-h-[44px] rounded-full border border-amber-500/50 px-4 py-2 text-xs font-bold text-amber-300" data-testid="delivery-slot-pause">
              Pause this slot
            </button>
          </div>
        ) : (
          <p className="mt-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="delivery-slot-none">
            No delivery slot published for this week — customers will see that delivery is unavailable and be offered collection.
          </p>
        )}

        {weekSlots.filter((s) => !s.active).map((s) => (
          <div key={s.id} className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-choco-line p-3 text-xs text-cream-muted" data-testid={`delivery-slot-paused-${s.id}`}>
            <span>Paused: {new Date(`${s.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}, {s.start_time}–{s.end_time} · capacity {s.capacity}</span>
            <button onClick={() => toggleSlot(s)} className="rounded-full border border-choco-line px-3 py-1.5 font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`delivery-slot-resume-${s.id}`}>Reopen</button>
          </div>
        ))}

        <div className="mt-5 border-t border-choco-line pt-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Publish or replace this week's slot</p>
          <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Delivery day">
            {[["saturday", "Saturday morning"], ["sunday", "Sunday morning"]].map(([value, label]) => (
              <button key={value} role="radio" aria-checked={day === value} onClick={() => setDay(value)}
                className={`min-h-[48px] rounded-full px-5 py-2.5 text-sm font-bold ${day === value ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:text-gold"}`}
                data-testid={`delivery-day-${value}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="mt-4 grid max-w-md grid-cols-3 gap-3">
            <div>
              <label htmlFor="dw-start" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Start</label>
              <input id="dw-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} className={`${inputClass} mt-1 w-full`} data-testid="delivery-start-time" />
            </div>
            <div>
              <label htmlFor="dw-end" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">End</label>
              <input id="dw-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={`${inputClass} mt-1 w-full`} data-testid="delivery-end-time" />
            </div>
            <div>
              <label htmlFor="dw-cap" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Max orders</label>
              <input id="dw-cap" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value.replace(/\D/g, "").slice(0, 2))} className={`${inputClass} mt-1 w-full`} data-testid="delivery-capacity" />
            </div>
          </div>
          <button onClick={publish} disabled={busy || !capacity}
            className="mt-4 min-h-[48px] rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco disabled:opacity-40"
            data-testid="delivery-publish-button">
            {busy ? "Publishing…" : published ? "Replace published slot" : "Publish delivery slot"}
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="delivery-rules-summary">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Your delivery areas</h2>
        <ul className="mt-3 space-y-2 text-sm text-cream-muted">
          <li data-testid="rule-zone1"><strong className="text-cream-text">B96</strong> — Free delivery — No minimum order</li>
          <li data-testid="rule-zone2"><strong className="text-cream-text">B60, B61, B49, B50 and CV37</strong> — £5 delivery — £40 minimum order</li>
          <li data-testid="rule-elsewhere"><strong className="text-cream-text">Everywhere else</strong> — Delivery by request or collection</li>
        </ul>
        <p className="mt-3 text-xs text-cream-muted">Change these any time under Delivery areas &amp; fees.</p>
      </div>
    </div>
  );
}

export default function AdminDeliveryCollection() {
  const [tab, setTab] = useState("collection");
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [confirmDelivery, setConfirmDelivery] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setDraft({ ...(r.data.collection || {}), delivery_enabled: r.data.delivery?.delivery_enabled === true });
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = () => {
    if (draft.delivery_enabled && !confirmDelivery) {
      toast.error("Tick the confirmation box to switch delivery on.");
      return;
    }
    api.put("/admin/settings", {
      collection: { public_area: draft.public_area || "", instructions: draft.instructions || "", private_address: draft.private_address || "" },
      delivery: { delivery_enabled: draft.delivery_enabled },
      base_updated_at: settings?.updated_at,
    }).then(() => { toast.success("Saved."); setConfirmDelivery(false); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div data-testid="admin-delivery-collection-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Delivery &amp; Collection</h1>

      <div className="no-print mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Delivery and collection sections">
        {TABS.map(([key, label, testid]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`min-h-[44px] rounded-full px-4 py-2 text-sm font-bold ${tab === key ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:text-gold"}`}
            data-testid={testid}>
            {label}
          </button>
        ))}
      </div>

      {tab === "collection" && draft && (
        <div className="mt-6 space-y-5" data-testid="collection-settings">
          <div className="rounded-3xl border border-choco-line bg-choco-light p-6">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Collection</h2>
            <p className="mt-1 text-xs text-cream-muted">Collection is always free. Customers see the general area before ordering; full collection details and instructions appear on their confirmed order page.</p>
            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="col-area" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Public collection area (shown to everyone)</label>
                <input id="col-area" value={draft.public_area || ""} onChange={(e) => setDraft({ ...draft, public_area: e.target.value })}
                  placeholder="e.g. Redditch, Worcestershire" className={`${inputClass} mt-1 w-full`} data-testid="collection-public-area" />
              </div>
              <div>
                <label htmlFor="col-instructions" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Collection instructions (shown after ordering)</label>
                <textarea id="col-instructions" rows={3} value={draft.instructions || ""} onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
                  placeholder="e.g. Please ring the doorbell and have your order number ready." className={`${inputClass} mt-1 w-full`} data-testid="collection-instructions" />
              </div>
              <div>
                <label htmlFor="col-address" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Collection address (shown on order confirmations and the legal pages)</label>
                <input id="col-address" value={draft.private_address || ""} onChange={(e) => setDraft({ ...draft, private_address: e.target.value })}
                  placeholder="Full collection address" className={`${inputClass} mt-1 w-full`} data-testid="collection-private-address" />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-amber-500/40 bg-choco-light p-6">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Delivery on/off</h2>
            <p className="mt-1 text-xs text-cream-muted">
              Delivery is <strong className={draft.delivery_enabled ? "text-green-400" : "text-amber-300"}>{draft.delivery_enabled ? "ON" : "OFF"}</strong>.
              While off, checkout offers collection only — even if zones below are marked active.
            </p>
            <label className="mt-4 flex min-h-[48px] items-center gap-3 text-sm font-semibold text-cream-text">
              <input type="checkbox" className="h-5 w-5" checked={draft.delivery_enabled} onChange={(e) => setDraft({ ...draft, delivery_enabled: e.target.checked })} data-testid="delivery-enabled-toggle" />
              Offer local delivery at checkout
            </label>
            {draft.delivery_enabled && (
              <label className="mt-2 flex min-h-[48px] items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
                <input type="checkbox" className="mt-0.5 h-5 w-5" checked={confirmDelivery} onChange={(e) => setConfirmDelivery(e.target.checked)} data-testid="delivery-confirm-checkbox" />
                I confirm the delivery zones, fees and minimum order below are correct and reviewed.
              </label>
            )}
          </div>

          <button onClick={save} className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco" data-testid="collection-save-button">
            <Save className="h-4 w-4" aria-hidden="true" /> Save
          </button>
        </div>
      )}

      {tab === "deliveryweek" && <WeeklyDelivery />}
      {tab === "slots" && <div className="mt-6" data-testid="slots-tab"><AdminSlots /></div>}
      {tab === "delivery" && <div className="mt-6" data-testid="delivery-tab"><AdminDelivery /></div>}
    </div>
  );
}
